<template>
  <div>
    <Heading
      :title="t('pages.hardsubToSubtitle.title')"
      :description="t('pages.hardsubToSubtitle.description')"
      icon="lucide:scan-text"
    />

    <div class="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
      <!-- ===== Toolbar ===== -->
      <div
        class="grid grid-cols-1 items-stretch gap-2.5 rounded-lg border bg-card p-3.5 shadow-sm md:grid-cols-2 md:gap-3 lg:grid-cols-[minmax(220px,1.6fr)_minmax(170px,1fr)_minmax(150px,0.9fr)_auto]"
      >
        <div v-if="activeSourceFileName" :class="[fileCellClass, 'border-primary/50']">
          <span :class="fileCellIconClass" aria-hidden="true">
            <Icon name="lucide:file-check-2" class="h-[18px] w-[18px]" />
          </span>
          <button
            type="button"
            :class="[fileCellTxtClass, 'flex-1 text-start disabled:cursor-not-allowed disabled:opacity-75']"
            :disabled="hasExistingJob || isUploading"
            @click="openFilePicker"
          >
            <b :class="fileCellTitleClass">{{ activeSourceFileName }}</b>
            <span :class="fileCellSubClass">{{ readableFileSize }}</span>
          </button>
          <button
            type="button"
            class="grid h-7 w-7 flex-none place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-75 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
            :disabled="hasExistingJob || isUploading"
            :aria-label="t('pages.audioToSubtitle.startOver')"
            @click="startOver"
          >
            <Icon name="lucide:x" class="h-4 w-4" />
          </button>
        </div>
        <button
          v-else
          type="button"
          :class="[fileCellClass, 'cursor-pointer border-input hover:border-primary/40 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-75']"
          :disabled="hasExistingJob || isUploading"
          @click="openFilePicker"
        >
          <span :class="fileCellIconClass" aria-hidden="true">
            <Icon name="lucide:plus" class="h-[18px] w-[18px]" />
          </span>
          <span :class="fileCellTxtClass">
            <b :class="fileCellTitleClass">{{ t("pages.hardsubToSubtitle.chooseVideo") }}</b>
            <span :class="fileCellSubClass">{{ t("pages.audioToSubtitle.maxFileSize", { size: maxSourceFileSizeLabel }) }}</span>
          </span>
        </button>

        <input
          id="hardsub-file"
          ref="fileInput"
          :accept="VIDEO_ACCEPT"
          type="file"
          class="hidden"
          @change="handleFileChange"
        />

        <div class="flex min-w-0 flex-col justify-between gap-1.5">
          <label for="hardsub-target-language" :class="toolbarLabelClass">{{ t("pages.subtitle_translator.targetLanguageLabel") }}</label>
          <NativeSelect
            id="hardsub-target-language"
            v-model="targetLanguage"
            class="h-[45px]"
            :disabled="hasExistingJob || isUploading"
          >
            <option :value="AUTO_TARGET_LANGUAGE">{{ t("pages.audioToSubtitle.targetLanguageAuto") }}</option>
            <optgroup v-if="favoriteLanguageOptions.length > 0" :label="t('pages.audioToSubtitle.targetLanguageFavorites')">
              <option v-for="languageOption in favoriteLanguageOptions" :key="languageOption.code" :value="languageOption.code">
                {{ languageOption.name }}
              </option>
            </optgroup>
            <optgroup :label="t('pages.audioToSubtitle.targetLanguageAll')">
              <option v-for="languageOption in otherLanguageOptions" :key="languageOption.code" :value="languageOption.code">
                {{ languageOption.name }}
              </option>
            </optgroup>
          </NativeSelect>
        </div>

        <div class="flex min-w-0 flex-col justify-between gap-1.5">
          <label for="hardsub-source-language" :class="toolbarLabelClass">{{ t("pages.hardsubToSubtitle.languageHint") }}</label>
          <NativeSelect
            id="hardsub-source-language"
            v-model="sourceLanguage"
            class="h-[45px]"
            :disabled="hasExistingJob || isUploading"
          >
            <option :value="AUTO_TARGET_LANGUAGE">{{ t("pages.hardsubToSubtitle.languageHintAuto") }}</option>
            <optgroup v-if="favoriteLanguageOptions.length > 0" :label="t('pages.audioToSubtitle.targetLanguageFavorites')">
              <option v-for="languageOption in favoriteLanguageOptions" :key="languageOption.code" :value="languageOption.code">
                {{ languageOption.name }}
              </option>
            </optgroup>
            <optgroup :label="t('pages.audioToSubtitle.targetLanguageAll')">
              <option v-for="languageOption in otherLanguageOptions" :key="languageOption.code" :value="languageOption.code">
                {{ languageOption.name }}
              </option>
            </optgroup>
          </NativeSelect>
        </div>

        <Button
          type="button"
          class="h-[45px] gap-2 self-end px-5 max-lg:w-full md:col-span-2 lg:col-span-1"
          :disabled="!canStart"
          @click="startJob"
        >
          <Icon v-if="isUploading" name="lucide:loader-circle" class="h-4 w-4 animate-spin" />
          <Icon v-else name="lucide:scan-text" class="h-4 w-4" />
          <span>{{ t("pages.hardsubToSubtitle.generateButton") }}</span>
        </Button>

        <div v-if="!selectedFile && !hasExistingJob" class="col-span-full flex min-w-0 flex-col gap-1.5">
          <label for="hardsub-link" :class="toolbarLabelClass">{{ t("pages.audioToSubtitle.mediaUrlLabel") }}</label>
          <div class="relative">
            <Input
              id="hardsub-link"
              v-model="mediaUrlInput"
              inputmode="url"
              autocomplete="off"
              spellcheck="false"
              class="h-[45px] pe-11"
              :placeholder="t('pages.audioToSubtitle.mediaUrlPlaceholder')"
              :disabled="isUploading"
            />
            <PasteClearButton v-model="mediaUrlInput" :disabled="isUploading" class="absolute end-1.5 top-[6px]" />
          </div>
        </div>
      </div>

      <!-- ===== Subtitle area (required) ===== -->
      <div v-if="showPicker" class="mt-3.5 rounded-lg border bg-card p-3.5 shadow-sm md:p-4">
        <div class="mb-1 flex items-center gap-[9px]">
          <Icon name="lucide:scan-text" class="h-[18px] w-[18px] text-primary" />
          <b class="text-base font-bold tracking-[-0.01em]">{{ t("pages.hardsubToSubtitle.regionTitle") }}</b>
        </div>
        <p :class="[stepDescClass, 'mb-3']">{{ t("pages.hardsubToSubtitle.regionHint") }}</p>
        <div class="mb-3 flex flex-wrap items-center gap-2" role="group" :aria-label="t('pages.hardsubToSubtitle.orientationLabel')">
          <span class="text-[13px] font-medium text-muted-foreground">{{ t("pages.hardsubToSubtitle.orientationLabel") }}</span>
          <Button
            v-for="option in ORIENTATION_OPTIONS"
            :key="option.value"
            type="button"
            size="sm"
            :variant="orientation === option.value ? 'soft' : 'outline'"
            class="gap-1.5"
            :aria-pressed="orientation === option.value"
            :disabled="isUploading"
            @click="setOrientation(option.value)"
          >
            <Icon :name="option.icon" class="h-4 w-4" aria-hidden="true" />
            <span>{{ t(option.label) }}</span>
          </Button>
        </div>
        <SubtitleRegionPicker
          v-model="region"
          :orientation="orientation"
          :file="selectedFile"
          :youtube-id="linkYoutubeId"
          :portrait="linkIsPortrait"
          :disabled="isUploading"
          @media="regionMedia = $event"
          @duration="onPickerDuration"
          @error="onPickerError"
        />
      </div>

      <!-- ===== Optional time range: shares the picker's player ===== -->
      <div v-if="showPicker && clipSource && user?.isPro" class="mt-3.5 rounded-lg border bg-card p-3.5 shadow-sm">
        <ClipRangeField
          v-model="clipRange"
          :source="clipSource"
          :duration-seconds="clipKnownDuration"
          :external-media="regionError ? null : regionMedia"
          show-timestamp-mode
          :disabled="isUploading"
          @duration="clipPlayerDuration = $event"
        />
      </div>

      <!-- ===== Access / credit notices ===== -->
      <ProGateNotice
        v-if="!user?.isPro"
        feature="media_subtitle"
        :title="t('pages.audioToSubtitle.proRequiredTitle')"
        :message="t('pages.audioToSubtitle.proRequiredDescription')"
        class="mt-3.5"
      />
      <template v-else>
        <p :class="[stepDescClass, 'flex flex-wrap items-center gap-x-1.5']">
          <Icon name="lucide:coins" class="h-3.5 w-3.5 flex-none align-[-2px] text-info" />
          <span>{{ t("pages.audioToSubtitle.creditAvailable", { count: availableCredits }) }}</span>
          <span v-if="estimatedCreditCost > 0">· {{ t("pages.audioToSubtitle.creditEstimate", { count: estimatedCreditCost }) }}</span>
        </p>
        <div v-if="isTooLong" :class="[noticeWarnClass, 'items-start']" role="alert">
          <Icon name="lucide:triangle-alert" :class="noticeIconClass" />
          <p class="min-w-0 flex-1 font-medium">{{ t("pages.hardsubToSubtitle.tooLong", { hours: HARDSUB_MAX_HOURS }) }}</p>
        </div>
        <div v-if="estimatedCreditCost > availableCredits" :class="[noticeWarnClass, 'flex-wrap gap-y-2.5']">
          <Icon name="lucide:triangle-alert" :class="noticeIconClass" />
          <p class="min-w-[180px] flex-1 font-medium">
            {{ t("pages.audioToSubtitle.creditInsufficient", { required: estimatedCreditCost, available: availableCredits }) }}
          </p>
          <Button
            type="button"
            size="sm"
            class="ms-auto flex-none gap-1.5"
            @click="openBuyCredits({ feature: 'media_subtitle', required: estimatedCreditCost, available: availableCredits })"
          >
            <Icon name="lucide:coins" class="h-3.5 w-3.5" />
            <span>{{ t("buttons.purchaseCredits") }}</span>
          </Button>
        </div>
      </template>

      <!-- ===== Resumable upload banner ===== -->
      <div v-if="showResumeBanner && resumableUpload" :class="noticeWarnClass">
        <Icon name="lucide:history" :class="noticeIconClass" />
        <div class="min-w-0">
          <p class="font-medium">{{ t("pages.audioToSubtitle.resumeTitle") }}</p>
          <p class="mt-0.5">
            {{ t("pages.audioToSubtitle.resumeDescription", {
              file: resumableUpload.meta.name,
              size: formatBytes(resumableUpload.meta.size),
              done: resumableUpload.state.parts.length,
              total: resumableUpload.meta.totalParts,
            }) }}
          </p>
          <div class="mt-2.5 flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" class="gap-1.5" :disabled="isUploading" @click="openFilePicker">
              <Icon name="lucide:play" class="h-3.5 w-3.5" />
              <span>{{ t("pages.audioToSubtitle.resumePick") }}</span>
            </Button>
            <Button type="button" variant="soft" size="sm" class="gap-1.5" :disabled="isUploading" @click="discardResumableUpload">
              <Icon name="lucide:trash-2" class="h-3.5 w-3.5" />
              <span>{{ t("pages.audioToSubtitle.resumeDiscard") }}</span>
            </Button>
          </div>
        </div>
      </div>

      <!-- Always-present live region: announces step changes, never percent ticks. -->
      <p class="sr-only" role="status" aria-live="polite">{{ liveStepText }}</p>

      <!-- ===== Workflow progress ===== -->
      <div v-if="showWorkflowCard" ref="workflowCardRef" :class="resultCardClass">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-[9px]">
            <Icon name="lucide:workflow" class="h-[18px] w-[18px] text-primary" />
            <b class="text-base font-bold tracking-[-0.01em]">{{ t("pages.audioToSubtitle.workflowTitle") }}</b>
          </div>
          <div class="flex items-center gap-2">
            <div v-if="isLoadingJob" class="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3.5 py-2.5 text-[13.5px] font-medium text-primary">
              <Icon name="lucide:loader-circle" class="h-[15px] w-[15px] flex-none animate-spin" />
              <span>{{ t("pages.audioToSubtitle.loadingJob") }}</span>
            </div>
            <Button v-if="isUploading && !isStartingLink" type="button" variant="outline" size="sm" class="flex-none gap-1.5" @click="cancelUpload">
              <Icon name="lucide:x" class="h-3.5 w-3.5" />
              <span>{{ t("pages.audioToSubtitle.batch.cancelItem") }}</span>
            </Button>
            <Button v-if="hasExistingJob" type="button" variant="soft" size="sm" class="flex-none gap-1.5" @click="startOver">
              <Icon name="lucide:plus" class="h-3.5 w-3.5" />
              <span>{{ t("pages.subtitleTasks.startAnother") }}</span>
            </Button>
          </div>
        </div>
        <div class="mx-0.5 my-3.5 h-px bg-border"></div>

        <MediaPreparingIndicator
          v-if="preparingPhase"
          :phase="preparingPhase"
          :started-at="preparingStartedAt"
        />
        <template v-else>
        <div class="mb-3 flex items-center gap-3">
          <span class="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-primary text-primary-foreground" aria-hidden="true">
            <Icon :name="stepView.icon" class="h-[15px] w-[15px]" />
          </span>
          <span class="text-base font-extrabold tracking-[-0.02em]">{{ stepView.title }}</span>
          <span :class="[statusPillClass, stepStatusClass]">
            <i class="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true"></i>{{ stepView.statusLabel }}
          </span>
          <span v-if="elapsedLabel" class="flex-none font-mono text-xs tabular-nums text-subtle-foreground" :aria-label="t('pages.audioToSubtitle.preparingElapsed')">
            <Icon name="lucide:timer" class="me-1 inline-block h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />{{ elapsedLabel }}
          </span>
          <span class="h-px flex-1 bg-border"></span>
        </div>
        <p v-if="stepView.description" :class="[stepDescClass, 'whitespace-pre-line break-words']">{{ stepView.description }}</p>

        <div v-if="stepView.progress !== null" class="mt-3">
          <div class="mb-1.5 flex items-center justify-between gap-2.5 text-[12.5px] text-subtle-foreground">
            <span>{{ stepView.progressLabel }}</span>
            <span class="font-mono">{{ stepView.progress }}%</span>
          </div>
          <div
            class="h-1.5 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="stepView.progress ?? undefined"
            :aria-label="stepView.progressLabel"
          >
            <div class="h-full rounded-full bg-primary transition-[width] duration-300" :style="{ width: `${stepView.progress}%` }"></div>
          </div>
          <p v-if="isUploading && uploadProgressBytes.total > 0" class="mt-1.5 font-mono text-xs text-subtle-foreground">
            {{ t("pages.audioToSubtitle.uploadProgressBytes", { loaded: formatBytes(uploadProgressBytes.loaded), total: formatBytes(uploadProgressBytes.total) }) }}
          </p>
        </div>
        <!-- Indeterminate phases: the shimmer travels toward the reading end -->
        <div
          v-else-if="stepView.loading"
          class="mt-3 h-1.5 overflow-hidden rounded-full bg-muted rtl:-scale-x-100"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="stepView.title"
        >
          <div class="h-full w-2/5 animate-shimmer rounded-full bg-primary/70 motion-reduce:w-full motion-reduce:animate-none motion-reduce:opacity-40"></div>
        </div>
        </template>

        <Button
          v-if="canAdjustRegion"
          type="button"
          variant="soft"
          size="sm"
          class="mt-3.5 gap-1.5"
          @click="adjustRegion"
        >
          <Icon name="lucide:scan-text" class="h-3.5 w-3.5" />
          <span>{{ t("pages.hardsubToSubtitle.adjustRegion") }}</span>
        </Button>
      </div>

      <!-- ===== Error (only when no workflow card carries it) ===== -->
      <div v-if="errorMessage && !showWorkflowCard" role="alert" :class="[noticeErrorClass, 'items-start']">
        <Icon name="lucide:circle-alert" :class="noticeIconClass" />
        <div class="min-w-0 flex-1">
          <p class="font-medium">{{ t("pages.audioToSubtitle.errors.errorTitle") }}</p>
          <p class="mt-0.5 whitespace-pre-line break-words">{{ errorMessage }}</p>
          <Button v-if="hasExistingJob" type="button" variant="soft" size="sm" class="mt-2.5 gap-1.5" @click="startOver">
            <Icon name="lucide:plus" class="h-3.5 w-3.5" />
            <span>{{ t("pages.subtitleTasks.startAnother") }}</span>
          </Button>
        </div>
      </div>

      <!-- ===== Result ===== -->
      <div v-if="showPreviewCard" :class="resultCardClass">
        <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div class="flex items-center gap-[9px]">
            <Icon name="lucide:captions" class="h-[18px] w-[18px] text-primary" />
            <b class="text-base font-bold tracking-[-0.01em]">{{ t("pages.audioToSubtitle.responseTitle") }}</b>
          </div>
          <div class="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
            <div class="flex items-center gap-2">
              <span class="shrink-0 text-[12.5px] font-medium text-subtle-foreground">{{ t("pages.subtitle_translator.outputFormatLabel") }}</span>
              <div class="flex flex-1 gap-2 sm:flex-none" role="group" :aria-label="t('pages.subtitle_translator.outputFormatLabel')">
                <button
                  v-for="fmt in SUBTITLE_FORMATS"
                  :key="fmt"
                  type="button"
                  :class="[chipRadioClass, 'flex-1 sm:flex-none', subtitleFormat === fmt ? chipRadioActiveClass : '']"
                  :aria-pressed="subtitleFormat === fmt"
                  :disabled="isDownloading"
                  @click="subtitleFormat = fmt"
                >{{ fmt.toUpperCase() }}</button>
              </div>
            </div>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
              <div class="flex gap-2">
                <Button type="button" variant="soft" class="h-10 flex-1 gap-2 sm:flex-none" @click="copyPreview">
                  <Icon name="lucide:copy" class="h-[18px] w-[18px]" /><span>{{ t("buttons.copy") }}</span>
                </Button>
                <Button type="button" class="h-10 flex-1 gap-2 sm:flex-none" :disabled="isDownloading" @click="downloadResult">
                  <Icon v-if="isDownloading" name="lucide:loader-circle" class="h-[18px] w-[18px] animate-spin" />
                  <Icon v-else name="lucide:file-down" class="h-[18px] w-[18px]" />
                  <span>{{ t("pages.audioToSubtitle.downloadButton") }}</span>
                </Button>
              </div>
              <Button type="button" variant="soft" class="h-10 gap-2 max-sm:w-full sm:flex-none" @click="startOver">
                <Icon name="lucide:plus" class="h-[18px] w-[18px]" /><span>{{ t("pages.subtitleTasks.startAnother") }}</span>
              </Button>
            </div>
          </div>
        </div>
        <div class="mx-0.5 my-3.5 h-px bg-border"></div>
        <pre :class="previewClass">{{ currentPreview || t("pages.audioToSubtitle.emptyState") }}</pre>
        <p :class="stepDescClass">{{ t("pages.audioToSubtitle.responseDescription") }}</p>
      </div>

      <!-- ===== Dropzone (empty state only; the picked file lives in the toolbar) ===== -->
      <div
        v-else-if="!hasExistingJob && !isUploading && !activeSourceFileName && !activePageUrl && !showResumeBanner"
        :class="dropzoneClass"
        @click="openFilePicker"
        @dragover.prevent="onDragOver"
        @dragenter.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <span class="mb-1.5 grid h-16 w-16 place-items-center rounded-lg bg-primary/10 text-primary" aria-hidden="true">
          <Icon name="lucide:scan-text" class="h-7 w-7" />
        </span>
        <h2 class="text-[19px] font-bold tracking-[-0.02em]">{{ t("pages.hardsubToSubtitle.dropTitle") }}</h2>
        <p class="max-w-[460px] text-sm text-muted-foreground">{{ t("pages.hardsubToSubtitle.dropDescription") }}</p>
        <div class="mb-1 mt-1.5 flex flex-wrap justify-center gap-2">
          <span v-for="ext in VIDEO_CHIPS" :key="ext" :class="chipClass">{{ ext }}</span>
          <span :class="chipClass">{{ maxSourceFileSizeLabel }}</span>
        </div>
        <Button type="button" class="mt-2 gap-2" @click.stop="openFilePicker">
          <Icon name="lucide:file-video" class="h-[18px] w-[18px]" />
          <span>{{ t("pages.hardsubToSubtitle.chooseVideo") }}</span>
        </Button>
      </div>

      <SubtitleTaskList v-if="user?.isPro" class="mt-4 sm:mt-6" :limit="5" :announce="false" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { toast } from "vue-sonner";
import { useUser, type ClientCredits } from "~/composables/useUser";
import { useMediaWorkflowErrors } from "~/composables/useMediaWorkflowErrors";
import { useSubtitleTasks } from "~/store/useSubtitleTasks";
import type { JobSnapshot, MediaPreparingPhase } from "~/types/media-subtitle";
import { extractYoutubeId, fetchYoutubeVideoInfo, type YoutubeVideoInfo } from "~/composables/useYoutubeDuration";
import { normalizeMediaPageUrl } from "~/utils/media-url-platforms";
import { AUTO_TARGET_LANGUAGE, GOOGLE_TRANSLATE_LANGUAGES } from "~/utils/languages";
import {
  DEFAULT_SUBTITLE_REGION,
  HARDSUB_MAX_SECONDS,
  defaultSubtitleRegion,
  type SubtitleOrientation,
  type SubtitleRegion,
} from "~/utils/hardsub-region";
import {
  MAX_SOURCE_FILE_SIZE_BYTES,
  SUBTITLE_TASK_RETENTION_DAYS,
  buildDerivedOutputTitle,
  buildMaxSourceFileSizeLabel,
  canDownloadFromJob,
  estimateCreditCost,
  formatBytes,
  getAvailableCredits,
  getConvertedPreview,
  getCurrentPreviewSourceFormat,
  getReadableFileSize,
} from "~/utils/media-subtitle-workflow";
import { getSubtitleMimeType, type SubtitleFormat } from "~/utils/subtitle-format";
import { saveBlob } from "~/utils/media-subtitle-download";
import {
  abortUpload,
  createSafeClientError,
  createUploadJob,
  createUrlJob,
  resolveMediaUrl,
  detectResumableUpload,
  isAbortError,
  resumeUpload,
  uploadFileChunked,
  type ClipRequest,
  type ResumableUploadCandidate,
  type UploadErrorMapper,
  type UploadProgress,
} from "~/utils/media-upload";
import {
  createClipRangeState,
  formatTimecode,
  normalizeClipRange,
  type ClipRangeSource,
  type ClipRangeState,
} from "~/utils/clip-range";

const { t, locale } = useI18n();
const { $encode } = useNuxtApp() as unknown as { $encode: (value: string) => string | false };
const route = useRoute();
const router = useRouter();
const { user, applyCredits } = useUser();
const { openBuyCredits } = useCreditShortfall();
const { trackUpsell } = useUpsellTracking();
const subtitleTasks = useSubtitleTasks();
const {
  getFileTooLargeErrorMessage,
  getFriendlyTranscriptErrorMessage,
  getPublicWorkflowErrorMessage,
  readErrorMessage,
  toastWorkflowError,
  getErrorMessage,
  uploadErrors,
} = useMediaWorkflowErrors();

const VIDEO_EXTENSIONS = ["mp4", "mov", "mkv", "webm", "m4v", "avi", "wmv"] as const;
const VIDEO_ACCEPT = VIDEO_EXTENSIONS.map((ext) => `.${ext}`).join(",");
const VIDEO_CHIPS = [".mp4", ".mov", ".mkv", ".webm"] as const;
const SUBTITLE_FORMATS = ["srt", "vtt", "txt"] as const;
const HARDSUB_MAX_HOURS = Math.round(HARDSUB_MAX_SECONDS / 3600);
const POLL_INTERVAL_MS = 4000;
const ORIENTATION_OPTIONS = [
  { value: "horizontal", label: "pages.hardsubToSubtitle.orientationHorizontal", icon: "lucide:rectangle-horizontal" },
  { value: "vertical", label: "pages.hardsubToSubtitle.orientationVertical", icon: "lucide:rectangle-vertical" },
] as const;
const maxSourceFileSizeLabel = buildMaxSourceFileSizeLabel(MAX_SOURCE_FILE_SIZE_BYTES);

/* ---------- Class constants (same vocabulary as MediaSubtitleWorkflow) ---------- */

const fileCellClass =
  "flex min-h-[46px] min-w-0 items-center gap-3 rounded-md border bg-muted px-3.5 py-[9px] text-start transition-colors";
const fileCellIconClass = "grid h-[34px] w-[34px] flex-none place-items-center rounded-sm bg-primary/10 text-primary";
const fileCellTxtClass = "flex min-w-0 flex-col gap-px";
const fileCellTitleClass = "block truncate text-sm font-semibold tracking-[-0.01em]";
const fileCellSubClass = "block truncate text-xs text-subtle-foreground";
const toolbarLabelClass = "font-mono text-[11px] uppercase tracking-[0.12em] text-subtle-foreground";
const stepDescClass = "mt-1 text-[13px] text-muted-foreground [overflow-wrap:anywhere]";
const noticeIconClass = "h-[18px] w-[18px] flex-none";
const noticeErrorClass =
  "mt-3.5 flex items-center gap-2.5 rounded-md border border-destructive/25 bg-destructive/10 px-3.5 py-3 text-[13.5px] leading-normal text-destructive";
const noticeWarnClass =
  "mt-3.5 flex items-center gap-2.5 rounded-md border border-warning/25 bg-warning/10 px-3.5 py-3 text-[13.5px] leading-normal text-warning";
const resultCardClass = "mt-4 animate-in fade-in rounded-lg border bg-card p-3.5 shadow-sm md:p-4";
const statusPillClass = "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-[11px] py-1 text-xs font-semibold";
const chipClass = "rounded-full border bg-muted px-2.5 py-1 font-mono text-xs tracking-wide text-muted-foreground";
const chipRadioClass =
  "rounded-md border border-input bg-muted px-[15px] py-[9px] font-mono text-[13px] font-semibold text-muted-foreground transition-colors hover:border-muted-foreground/60 hover:text-foreground disabled:pointer-events-none disabled:opacity-55 [@media(pointer:coarse)]:min-h-11";
const chipRadioActiveClass = "border-primary bg-primary text-primary-foreground hover:border-primary hover:text-primary-foreground";
const previewClass =
  "m-0 max-h-[440px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/60 p-3.5 font-mono text-[12.5px] leading-relaxed";

/* ---------- Languages ---------- */

const supportedLanguages = Object.entries(GOOGLE_TRANSLATE_LANGUAGES).map(([code, name]) => ({ code, name }));
// The user's Preference languages (UI locale when none) lead both lists.
const favoriteLanguageOptions = computed(() => {
  const preferred: string[] = user.value?.favorite_languages?.length ? user.value.favorite_languages : [locale.value];
  const matched = preferred
    .map((code) =>
      supportedLanguages.find(
        (option) => option.code.toLowerCase() === code.toLowerCase() || option.code.split("-")[0] === code.split("-")[0]
      )
    )
    .filter((option): option is (typeof supportedLanguages)[number] => Boolean(option));
  return [...new Map(matched.map((option) => [option.code, option])).values()];
});
const otherLanguageOptions = computed(() => {
  const favoriteCodes = new Set(favoriteLanguageOptions.value.map((option) => option.code));
  return supportedLanguages.filter((option) => !favoriteCodes.has(option.code));
});

/* ---------- State ---------- */

const fileInput = ref<HTMLInputElement | null>(null);
const workflowCardRef = ref<HTMLElement | null>(null);
const selectedFile = ref<File | null>(null);
const durationSeconds = ref(0);
const region = ref<SubtitleRegion>({ ...DEFAULT_SUBTITLE_REGION });
const orientation = ref<SubtitleOrientation>("horizontal");

// Each direction lives in a different part of the frame, so the box follows.
const setOrientation = (next: SubtitleOrientation) => {
  if (next === orientation.value) return;
  orientation.value = next;
  region.value = { ...defaultSubtitleRegion(next) };
};
const regionMedia = ref<HTMLVideoElement | null>(null);
// Client-side picker failure ("UNPLAYABLE" / "NO_VIDEO_TRACK"); blocks Generate.
const regionError = ref("");
const targetLanguage = ref(AUTO_TARGET_LANGUAGE);
const sourceLanguage = ref(AUTO_TARGET_LANGUAGE);
const clipRange = ref<ClipRangeState>(createClipRangeState());
const clipPlayerDuration = ref(0);
const subtitleFormat = ref<SubtitleFormat>("srt");
const isDragOver = ref(false);

const errorMessage = ref("");
const isUploading = ref(false);
const mediaUrlInput = ref("");
const isStartingLink = ref(false);
const linkStartedAt = ref(0);
const linkInfo = ref<YoutubeVideoInfo>({ durationSeconds: 0, title: "" });
const isResuming = ref(false);
const uploadProgress = ref(0);
const uploadProgressBytes = ref({ loaded: 0, total: 0 });
const uploadAbortController = ref<AbortController | null>(null);
const resumableUpload = ref<ResumableUploadCandidate | null>(null);
const isLoadingJob = ref(false);
const isDownloading = ref(false);
const jobSnapshot = ref<JobSnapshot | null>(null);
let pollingTimer: ReturnType<typeof setTimeout> | null = null;
let isUnmounted = false;

/* ---------- Derived ---------- */

const hasExistingJob = computed(() => Boolean(jobSnapshot.value?.jobId));
const routeJobId = computed(() => (typeof route.query.job === "string" ? route.query.job.trim() : ""));
const availableCredits = computed(() => getAvailableCredits(user.value?.monthly_limit, user.value?.bonus_limit));
const activeSourceFileName = computed(() => selectedFile.value?.name || jobSnapshot.value?.sourceFileName || "");
const readableFileSize = computed(() => getReadableFileSize(selectedFile.value?.size || jobSnapshot.value?.sourceFileSize || 0));
const showResumeBanner = computed(() => Boolean(resumableUpload.value && !isResuming.value));
const activePageUrl = computed(() => (selectedFile.value ? "" : normalizeMediaPageUrl(mediaUrlInput.value)));
const linkYoutubeId = computed(() => (activePageUrl.value && extractYoutubeId(activePageUrl.value)) || "");
const linkIsPortrait = computed(() => activePageUrl.value.includes("/shorts/"));
const showPicker = computed(() =>
  Boolean(selectedFile.value || activePageUrl.value) &&
  !hasExistingJob.value &&
  !isUploading.value &&
  !showResumeBanner.value
);
const preparingPhase = computed<MediaPreparingPhase | null>(() => {
  if (isStartingLink.value) return "link";
  const job = jobSnapshot.value;
  if (!job || job.status === "failed" || !job.fetchPhase) return null;
  return job.fetchPhase === "downloading" ? "downloading" : "resolving";
});
const preparingStartedAt = computed(() => linkStartedAt.value || Date.parse(jobSnapshot.value?.createdAt || "") || Date.now());

const derivedOutputTitle = computed(() =>
  buildDerivedOutputTitle({
    snapshotTitle: jobSnapshot.value?.outputTitle,
    sourceFileName: selectedFile.value?.name || jobSnapshot.value?.sourceFileName,
    fallback: "subtitle",
  })
);

const clipSource = computed<ClipRangeSource | null>(() => {
  if (selectedFile.value) return { kind: "file", file: selectedFile.value };
  if (!activePageUrl.value) return null;
  return linkYoutubeId.value ? { kind: "youtube", videoId: linkYoutubeId.value } : { kind: "none" };
});
const clipKnownDuration = computed(() =>
  durationSeconds.value || linkInfo.value.durationSeconds || clipPlayerDuration.value
);
const activeClip = computed(() =>
  showPicker.value && clipRange.value.enabled
    ? normalizeClipRange(clipRange.value, clipKnownDuration.value, { allowUnknownDuration: true })
    : ({ ok: true, clip: null } as const)
);
const clipRequest = computed<ClipRequest | undefined>(() => {
  const clip = activeClip.value.ok ? activeClip.value.clip : null;
  return clip
    ? { startSeconds: clip.startSeconds, endSeconds: clip.endSeconds, timestampMode: clipRange.value.timestampMode }
    : undefined;
});
const isClipInvalid = computed(() => showPicker.value && clipRange.value.enabled && !activeClip.value.ok);

// Seconds the job will read (and be billed for): the clip when there is one.
const billedSeconds = computed(() => {
  if (!hasExistingJob.value && clipRequest.value) {
    return clipRequest.value.endSeconds - clipRequest.value.startSeconds;
  }
  return jobSnapshot.value?.durationSeconds || clipKnownDuration.value;
});
const estimatedCreditCost = computed(() => estimateCreditCost(billedSeconds.value));
// Reading every frame is CPU-bound: past the cap the user must pick a range.
const isTooLong = computed(() => !hasExistingJob.value && billedSeconds.value > HARDSUB_MAX_SECONDS);

const canStart = computed(() =>
  Boolean(user.value?.isPro) &&
  Boolean(selectedFile.value || activePageUrl.value) &&
  !hasExistingJob.value &&
  !isUploading.value &&
  !isLoadingJob.value &&
  !regionError.value &&
  !isClipInvalid.value &&
  !isTooLong.value &&
  (estimatedCreditCost.value === 0 || availableCredits.value >= estimatedCreditCost.value)
);

const currentPreview = computed(() =>
  getConvertedPreview(
    jobSnapshot.value?.activePreview || "",
    getCurrentPreviewSourceFormat(jobSnapshot.value?.format),
    subtitleFormat.value
  )
);
const canDownload = computed(() => canDownloadFromJob(jobSnapshot.value));
const showPreviewCard = computed(() =>
  Boolean(currentPreview.value && canDownload.value && jobSnapshot.value?.status === "ready")
);
const showWorkflowCard = computed(() => {
  if (showResumeBanner.value) return false;
  if (showPreviewCard.value) return false;
  return Boolean(hasExistingJob.value || isLoadingJob.value || isUploading.value || routeJobId.value);
});
const canAdjustRegion = computed(() =>
  jobSnapshot.value?.status === "failed" && jobSnapshot.value?.error.code === "NO_TEXT_FOUND" && Boolean(selectedFile.value)
);

// Count the "this job needs more credits than you have" moment once per page.
watch(
  () => !!user.value?.isPro && estimatedCreditCost.value > availableCredits.value,
  (short) => {
    if (short) trackUpsell("credit_block", { feature: "media_subtitle" });
  }
);

// Title and length of a pasted YouTube link: the file's own metadata stands in
// for these on an upload.
watch(linkYoutubeId, async (videoId) => {
  linkInfo.value = { durationSeconds: 0, title: "" };
  if (!videoId) return;
  const info = await fetchYoutubeVideoInfo(videoId, $encode);
  if (videoId === linkYoutubeId.value) linkInfo.value = info;
});

watch(activePageUrl, () => {
  clipRange.value = createClipRangeState();
  clipPlayerDuration.value = 0;
});

// A different file starts from the default band and the whole video again.
watch(selectedFile, () => {
  orientation.value = "horizontal";
  region.value = { ...DEFAULT_SUBTITLE_REGION };
  regionError.value = "";
  clipRange.value = createClipRangeState();
  clipPlayerDuration.value = 0;
});

/* ---------- Step view ---------- */

type StepView = {
  icon: string;
  title: string;
  description: string;
  statusLabel: string;
  loading: boolean;
  failed: boolean;
  ready: boolean;
  progress: number | null;
  progressLabel: string;
};

const stepView = computed<StepView>(() => {
  const processing = t("pages.audioToSubtitle.statusProcessing");
  const base = {
    description: "",
    loading: true,
    failed: false,
    ready: false,
    progress: null as number | null,
    progressLabel: "",
    statusLabel: processing,
  };

  if (isUploading.value) {
    return {
      ...base,
      icon: "lucide:upload",
      title: t("pages.hardsubToSubtitle.phases.uploading"),
      description: t("pages.audioToSubtitle.mediaUploadStepDescription"),
      progress: uploadProgress.value,
      progressLabel: t("pages.audioToSubtitle.uploadProgressLabel"),
    };
  }

  const job = jobSnapshot.value;
  if (!job) {
    return {
      ...base,
      icon: "lucide:upload",
      title: t("pages.hardsubToSubtitle.phases.uploading"),
      statusLabel: t("pages.audioToSubtitle.statusPending"),
      loading: false,
    };
  }

  if (job.status === "failed") {
    return {
      ...base,
      icon: "lucide:triangle-alert",
      title: t("pages.audioToSubtitle.failedStepTitle"),
      description: errorMessage.value || t("pages.audioToSubtitle.errors.failed"),
      statusLabel: t("pages.audioToSubtitle.statusError"),
      loading: false,
      failed: true,
    };
  }

  if (job.status === "ready") {
    return {
      ...base,
      icon: "lucide:circle-check-big",
      title: t("pages.hardsubToSubtitle.phases.ready"),
      description: t("pages.audioToSubtitle.autoReadyDescription"),
      statusLabel: t("pages.audioToSubtitle.statusReady"),
      loading: false,
      ready: true,
    };
  }

  const translating = job.status === "translating" || job.translateStatus === "processing";
  if (translating && job.transcriptStatus === "done") {
    const progress = job.translateProgress;
    return {
      ...base,
      icon: "lucide:languages",
      title: t("pages.hardsubToSubtitle.phases.translating"),
      description: t("pages.audioToSubtitle.mediaAutoTranslateDescription"),
      progress: progress.total > 0 ? progress.percentage || 0 : null,
      progressLabel:
        progress.total > 0
          ? t("pages.audioToSubtitle.translateProgressCount", { current: progress.current, total: progress.total })
          : t("pages.audioToSubtitle.translateProgressLabel"),
    };
  }

  const ocr = job.ocrProgress;
  if (ocr?.phase === "read") {
    return {
      ...base,
      icon: "lucide:scan-text",
      title: t("pages.hardsubToSubtitle.phases.reading", { current: ocr.current, total: ocr.total }),
      progress: ocr.total > 0 ? Math.min(100, Math.max(0, Math.round(ocr.percentage || 0))) : null,
      progressLabel: t("pages.hardsubToSubtitle.phases.reading", { current: ocr.current, total: ocr.total }),
    };
  }
  if (ocr?.phase === "finalize") {
    return { ...base, icon: "lucide:captions", title: t("pages.hardsubToSubtitle.phases.finalizing") };
  }
  if (ocr?.phase === "extract") {
    // The container reports decoded frames; before the probe finishes the
    // total is unknown and the bar stays indeterminate.
    return {
      ...base,
      icon: "lucide:film",
      title: t("pages.hardsubToSubtitle.phases.extracting"),
      progress: ocr.total > 0 ? Math.min(99, Math.max(0, Math.round(ocr.percentage || 0))) : null,
      progressLabel: t("pages.hardsubToSubtitle.phases.extracting"),
    };
  }
  return {
    ...base,
    icon: "lucide:hourglass",
    title: t("pages.hardsubToSubtitle.phases.queued"),
    statusLabel: t("pages.audioToSubtitle.statusPending"),
  };
});

// Elapsed time since the job was created, ticking while it runs, so a long
// extract never looks frozen even between progress updates.
const nowMs = ref(Date.now());
let elapsedTimer: ReturnType<typeof setInterval> | null = null;
const stopElapsedTimer = () => {
  if (elapsedTimer) {
    clearInterval(elapsedTimer);
    elapsedTimer = null;
  }
};
watch(
  () => Boolean(showWorkflowCard.value && stepView.value.loading && !isUploading.value),
  (running) => {
    stopElapsedTimer();
    if (!running || !import.meta.client) return;
    nowMs.value = Date.now();
    elapsedTimer = setInterval(() => {
      nowMs.value = Date.now();
    }, 1000);
  },
  { immediate: true }
);
const elapsedLabel = computed(() => {
  const job = jobSnapshot.value;
  if (!job || !stepView.value.loading || isUploading.value) return "";
  const startedAt = Date.parse(job.createdAt);
  if (!Number.isFinite(startedAt)) return "";
  return formatTimecode(Math.max(0, (nowMs.value - startedAt) / 1000));
});

const stepStatusClass = computed(() => {
  if (stepView.value.failed) return "bg-destructive/10 text-destructive";
  if (stepView.value.ready) return "bg-primary/10 text-primary";
  if (stepView.value.loading) return "bg-warning/15 text-warning";
  return "bg-muted text-subtle-foreground";
});

// Screen-reader step announcement (titles only; percent ticks stay silent).
const liveStepText = computed(() => {
  if (!showWorkflowCard.value) return "";
  const view = stepView.value;
  if (view.failed) return `${view.title}: ${view.description}`;
  return `${view.title} - ${view.statusLabel}`;
});

const dropzoneClass = computed(() => [
  "mt-4 flex min-h-[320px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-[1.5px] border-dashed bg-card px-6 py-10 text-center transition-colors",
  isDragOver.value ? "border-primary bg-primary/5" : "border-input",
]);

/* ---------- Errors ---------- */

const OCR_ERROR_KEYS: Record<string, string> = {
  NO_TEXT_FOUND: "pages.hardsubToSubtitle.errors.noTextFound",
  NO_VIDEO_TRACK: "pages.hardsubToSubtitle.errors.noVideoTrack",
  REGION_INVALID: "pages.hardsubToSubtitle.errors.regionInvalid",
  OCR_TOO_LONG: "pages.hardsubToSubtitle.errors.tooLong",
  OCR_NEEDS_VIDEO: "pages.hardsubToSubtitle.errors.needsVideo",
  OCR_FAILED: "pages.hardsubToSubtitle.errors.ocrFailed",
};

function ocrErrorCopy(code: string | null | undefined) {
  const key = code ? OCR_ERROR_KEYS[code] : undefined;
  return key ? t(key, { hours: HARDSUB_MAX_HOURS }) : "";
}

/** Error copy for a job snapshot, keyed on the platform code first. */
function describeJobError(job: JobSnapshot) {
  const code = job.error.code;
  const ocrCopy = ocrErrorCopy(code);
  if (ocrCopy) return ocrCopy;
  if (code === "FILE_TOO_LARGE") return getFileTooLargeErrorMessage();
  if (code === "INSUFFICIENT_CREDITS") return t("pages.subtitleTasks.unpaidError", { days: SUBTITLE_TASK_RETENTION_DAYS });
  if (code === "OUTPUT_EXPIRED") return t("pages.subtitleTasks.outputExpiredError");
  return job.error.message ? getPublicWorkflowErrorMessage(job.error.message) : "";
}

// The create endpoint refuses with `data.code` (REGION_INVALID, OCR_NEEDS_VIDEO,
// OCR_TOO_LONG); read that before falling back to the shared message mapping.
function codeFromErrorBody(rawText: string) {
  try {
    const parsed = JSON.parse(rawText) as { code?: string; data?: { code?: string } };
    return parsed?.data?.code || parsed?.code || "";
  } catch {
    return "";
  }
}

const ocrUploadErrors: UploadErrorMapper = {
  uploadFailed: uploadErrors.uploadFailed,
  fromText: (rawText) => ocrErrorCopy(codeFromErrorBody(rawText)) || uploadErrors.fromText(rawText),
  fromResponse: async (response) => {
    const rawText = await response.text().catch(() => "");
    return ocrErrorCopy(codeFromErrorBody(rawText)) || getFriendlyTranscriptErrorMessage(response.status, rawText);
  },
};

function setError(message: string) {
  errorMessage.value = message;
}

function onPickerError(code: string) {
  regionError.value = code;
  setError(ocrErrorCopy(code) || t("pages.hardsubToSubtitle.errors.unplayable"));
}

function onPickerDuration(seconds: number) {
  durationSeconds.value = seconds;
}

/* ---------- Polling ---------- */

function shouldPoll(job: JobSnapshot | null) {
  if (!job || job.status === "failed" || job.status === "ready") return false;
  // An upload that never finished is resumed, not polled.
  return job.uploadStatus === "done";
}

function clearPolling() {
  if (pollingTimer) {
    clearTimeout(pollingTimer);
    pollingTimer = null;
  }
}

function schedulePolling() {
  clearPolling();
  if (isUnmounted || !jobSnapshot.value?.jobId || !shouldPoll(jobSnapshot.value)) return;
  pollingTimer = setTimeout(async () => {
    try {
      await loadJobStatus(jobSnapshot.value!.jobId, false);
    } catch (error) {
      console.error(error);
    } finally {
      schedulePolling();
    }
  }, POLL_INTERVAL_MS);
}

function hydrateJob(job: JobSnapshot) {
  jobSnapshot.value = job;
  subtitleFormat.value = "srt";
  durationSeconds.value = job.durationSeconds || durationSeconds.value;
  if (job.targetLanguage) targetLanguage.value = job.targetLanguage;
  errorMessage.value = job.status === "failed" ? describeJobError(job) : "";
  schedulePolling();
  // Mirror the job into the shared task store so it shows in the background
  // list and keeps being polled after the user starts another task.
  subtitleTasks.upsertFromSnapshot(job);
}

async function loadJobStatus(jobId: string, showLoading = true) {
  if (!jobId) return;
  if (showLoading) isLoadingJob.value = true;
  try {
    const response = await fetch(`/api/video-to-subtitle/platform/status?job=${encodeURIComponent(jobId)}`);
    if (!response.ok) {
      throw createSafeClientError(await readErrorMessage(response));
    }
    const data = (await response.json()) as { job: JobSnapshot; credits?: ClientCredits };
    // Sent once the job is final (credits are charged when it turns ready).
    applyCredits(data.credits);
    hydrateJob(data.job);
  } finally {
    isLoadingJob.value = false;
  }
}

/* ---------- File picking ---------- */

function isVideoFile(file: File) {
  if (file.type.startsWith("video/")) return true;
  if (file.type.startsWith("audio/")) return false;
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  return VIDEO_EXTENSIONS.includes(extension as (typeof VIDEO_EXTENSIONS)[number]);
}

function openFilePicker() {
  fileInput.value?.click();
}

async function processPickedFile(file: File | null, input?: HTMLInputElement) {
  if (!file) return;

  if (!isVideoFile(file)) {
    setError(t("pages.hardsubToSubtitle.errors.videoOnly"));
    toast.error(errorMessage.value);
    if (input) input.value = "";
    return;
  }

  if (file.size > MAX_SOURCE_FILE_SIZE_BYTES) {
    setError(getFileTooLargeErrorMessage());
    toast.error(errorMessage.value);
    if (input) input.value = "";
    return;
  }

  const candidate = resumableUpload.value;
  if (candidate) {
    const matches =
      file.name === candidate.meta.name && file.size === candidate.meta.size && file.lastModified === candidate.meta.lastModified;
    if (!matches) {
      if (input) input.value = "";
      const message = t("pages.audioToSubtitle.errors.resumeMismatch", {
        file: `${candidate.meta.name} (${formatBytes(candidate.meta.size)})`,
      });
      setError(message);
      toast.error(message);
      return;
    }
    errorMessage.value = "";
    await startResumeUpload(candidate, file);
    if (input) input.value = "";
    return;
  }

  if (hasExistingJob.value) await startOver();

  selectedFile.value = file;
  durationSeconds.value = 0;
  errorMessage.value = "";
}

async function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  await processPickedFile(input.files?.[0] || null, input);
}

function onDragOver() {
  if (hasExistingJob.value || isUploading.value) return;
  isDragOver.value = true;
}

function onDragLeave(event: DragEvent) {
  // Crossing into a child fires dragleave on the parent; keep the highlight.
  const zone = event.currentTarget as HTMLElement | null;
  if (zone && event.relatedTarget instanceof Node && zone.contains(event.relatedTarget)) return;
  isDragOver.value = false;
}

async function onDrop(event: DragEvent) {
  isDragOver.value = false;
  if (hasExistingJob.value || isUploading.value) return;
  const file = event.dataTransfer?.files?.[0] || null;
  if (file) await processPickedFile(file);
}

/* ---------- Lifecycle of a job ---------- */

function applyUploadProgress(progress: UploadProgress) {
  uploadProgress.value = progress.percent;
  uploadProgressBytes.value = { loaded: progress.loaded, total: progress.total };
}

function resetTransientState() {
  errorMessage.value = "";
  isUploading.value = false;
  uploadProgress.value = 0;
  uploadProgressBytes.value = { loaded: 0, total: 0 };
  isDownloading.value = false;
  isLoadingJob.value = false;
}

async function removeJobFromQuery() {
  if (!route.query.job) return;
  const nextQuery = { ...route.query };
  delete nextQuery.job;
  await router.replace({ path: route.path, query: nextQuery });
}

function resetLocalSelection() {
  selectedFile.value = null;
  mediaUrlInput.value = "";
  durationSeconds.value = 0;
  if (fileInput.value) fileInput.value.value = "";
}

async function startOver() {
  clearPolling();
  jobSnapshot.value = null;
  resetLocalSelection();
  resetTransientState();
  targetLanguage.value = AUTO_TARGET_LANGUAGE;
  sourceLanguage.value = AUTO_TARGET_LANGUAGE;
  subtitleFormat.value = "srt";
  await removeJobFromQuery();
}

/** NO_TEXT_FOUND: back to the picker with the same file, box and range. */
async function adjustRegion() {
  clearPolling();
  jobSnapshot.value = null;
  resetTransientState();
  await removeJobFromQuery();
}

function scrollToWorkflowCard(retryCount = 0) {
  nextTick(() => {
    const target = workflowCardRef.value;
    if (!target) {
      // On mobile, the workflow card can mount slightly later.
      if (retryCount < 5) window.setTimeout(() => scrollToWorkflowCard(retryCount + 1), 120);
      return;
    }
    const scrollOffset = window.innerWidth < 640 ? 96 : 80;
    window.scrollTo({ top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - scrollOffset), behavior: "smooth" });
  });
}

function cancelUpload() {
  uploadAbortController.value?.abort();
}

async function startLinkJob(
  pageUrl: string,
  request: { clip?: ClipRequest; ocrRegion: SubtitleRegion; durationSeconds: number }
) {
  isUploading.value = true;
  isStartingLink.value = true;
  linkStartedAt.value = Date.now();
  errorMessage.value = "";
  scrollToWorkflowCard();

  try {
    const resolved = await resolveMediaUrl(
      {
        sourceUrl: pageUrl,
        title: linkInfo.value.title,
        durationSeconds: request.durationSeconds,
        clip: request.clip,
        tool: "ocr",
      },
      { errors: ocrUploadErrors }
    );
    const job = await createUrlJob(
      {
        resolved: resolved.resolved,
        signature: resolved.signature,
        targetLanguage: targetLanguage.value || "",
        clip: request.clip,
        tool: "ocr",
        ocrRegion: request.ocrRegion,
        ocrOrientation: orientation.value,
        sourceLanguage: sourceLanguage.value === AUTO_TARGET_LANGUAGE ? undefined : sourceLanguage.value,
      },
      { errors: ocrUploadErrors }
    );
    await router.replace({ path: route.path, query: { ...route.query, job: job.jobId } });
    hydrateJob(job);
  } catch (error) {
    const message = getErrorMessage(error);
    setError(message);
    toastWorkflowError(message);
  } finally {
    isUploading.value = false;
    isStartingLink.value = false;
    linkStartedAt.value = 0;
  }
}

async function startJob() {
  if (!user.value?.isPro) {
    setError(t("pages.audioToSubtitle.errors.proRequired"));
    return;
  }
  const file = selectedFile.value;
  const pageUrl = activePageUrl.value;
  if (!file && !pageUrl) {
    setError(t("pages.hardsubToSubtitle.errors.missingFile"));
    return;
  }
  if (regionError.value) {
    setError(ocrErrorCopy(regionError.value) || t("pages.hardsubToSubtitle.errors.unplayable"));
    return;
  }
  if (isTooLong.value) {
    setError(t("pages.hardsubToSubtitle.tooLong", { hours: HARDSUB_MAX_HOURS }));
    return;
  }
  if (estimatedCreditCost.value > availableCredits.value) {
    setError(t("pages.audioToSubtitle.errors.notEnoughCredits"));
    return;
  }

  // Snapshot the request before flipping isUploading: showPicker (and so
  // clipRequest) turns off as soon as the upload starts.
  const clip = clipRequest.value;
  const ocrRegion = { ...region.value };
  const durationForJob = clipKnownDuration.value || 0;

  if (!file) {
    await startLinkJob(pageUrl, { clip, ocrRegion, durationSeconds: durationForJob });
    return;
  }

  uploadAbortController.value?.abort();
  const abortController = new AbortController();
  uploadAbortController.value = abortController;

  isUploading.value = true;
  uploadProgress.value = 0;
  uploadProgressBytes.value = { loaded: 0, total: 0 };
  errorMessage.value = "";
  scrollToWorkflowCard();

  try {
    const signedUploadData = await createUploadJob(
      {
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        fileSize: file.size,
        durationSeconds: durationForJob,
        title: derivedOutputTitle.value,
        targetLanguage: targetLanguage.value || "",
        clip,
        tool: "ocr",
        ocrRegion,
        ocrOrientation: orientation.value,
        sourceLanguage: sourceLanguage.value === AUTO_TARGET_LANGUAGE ? undefined : sourceLanguage.value,
      },
      { errors: ocrUploadErrors, signal: abortController.signal }
    );

    // Persist the jobId in the URL immediately so a mid-upload reload can
    // recover and resume.
    await router.replace({ path: route.path, query: { ...route.query, job: signedUploadData.jobId } });

    const job = await uploadFileChunked(signedUploadData.jobId, file, {
      signal: abortController.signal,
      errors: ocrUploadErrors,
      onProgress: applyUploadProgress,
    });
    hydrateJob(job);
  } catch (error) {
    if (isAbortError(error)) {
      // The multipart session survives on purpose so the upload can resume.
      return;
    }
    const message = getErrorMessage(error);
    setError(message);
    toastWorkflowError(message);
  } finally {
    isUploading.value = false;
    if (uploadAbortController.value === abortController) uploadAbortController.value = null;
  }
}

async function startResumeUpload(candidate: ResumableUploadCandidate, file: File) {
  uploadAbortController.value?.abort();
  const abortController = new AbortController();
  uploadAbortController.value = abortController;

  isUploading.value = true;
  isResuming.value = true;
  uploadProgress.value = 0;
  uploadProgressBytes.value = { loaded: 0, total: file.size };
  errorMessage.value = "";
  scrollToWorkflowCard();

  try {
    const job = await resumeUpload(candidate.jobId, file, candidate.meta, candidate.state, {
      signal: abortController.signal,
      errors: ocrUploadErrors,
      onProgress: applyUploadProgress,
    });
    resumableUpload.value = null;
    selectedFile.value = file;
    hydrateJob(job);
  } catch (error) {
    if (isAbortError(error)) return;
    const message = getErrorMessage(error);
    setError(message);
    toastWorkflowError(message);
  } finally {
    isUploading.value = false;
    isResuming.value = false;
    if (uploadAbortController.value === abortController) uploadAbortController.value = null;
  }
}

async function discardResumableUpload() {
  const candidate = resumableUpload.value;
  if (!candidate) return;
  resumableUpload.value = null;
  void abortUpload(candidate.jobId);
  jobSnapshot.value = null;
  resetTransientState();
  resetLocalSelection();
  await removeJobFromQuery();
}

/* ---------- Result ---------- */

function downloadResult() {
  if (!jobSnapshot.value?.jobId) return;
  if (!currentPreview.value) {
    toast.error(t("pages.subtitleTasks.downloadEmpty"));
    return;
  }
  isDownloading.value = true;
  try {
    saveBlob(
      new Blob([currentPreview.value], { type: getSubtitleMimeType(subtitleFormat.value) }),
      `${derivedOutputTitle.value}.${subtitleFormat.value}`
    );
  } catch (error) {
    const message = getErrorMessage(error);
    setError(message);
    toast.error(message);
  } finally {
    isDownloading.value = false;
  }
}

async function copyPreview() {
  if (!currentPreview.value) return;
  try {
    await navigator.clipboard.writeText(currentPreview.value);
    toast.success(t("announcements.copied"));
  } catch (error) {
    console.error(error);
    toast.error(t("pages.audioToSubtitle.errors.copyFailed"));
  }
}

/* ---------- ?job= resume ---------- */

watch(
  () => route.query.job,
  async (jobId) => {
    const normalizedJobId = typeof jobId === "string" ? jobId.trim() : "";
    if (!normalizedJobId) {
      clearPolling();
      resumableUpload.value = null;
      return;
    }
    if (jobSnapshot.value?.jobId === normalizedJobId) {
      schedulePolling();
      return;
    }
    if (isUploading.value) return;
    try {
      await loadJobStatus(normalizedJobId);
      resumableUpload.value = (await detectResumableUpload(normalizedJobId)) ?? null;
    } catch (error) {
      const message = getErrorMessage(error);
      setError(message);
      toast.error(message);
      await removeJobFromQuery();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  isUnmounted = true;
  clearPolling();
  stopElapsedTimer();
  if (uploadAbortController.value) {
    uploadAbortController.value.abort();
    uploadAbortController.value = null;
  }
});
</script>
